<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import IconClose from '../icons/IconClose.vue';
import IconFile from '../icons/IconFile.vue';
import IconLock from '../icons/IconLock.vue';
import IconSearch from '../icons/IconSearch.vue';
import XlButton from '../xl/XlButton.vue';
import PortalLibraryRow from './PortalLibraryRow.vue';
import Loading from '../Loading.vue';
import usePortalLibrary from '../../composables/usePortalLibrary';
import useLibraryMarks from '../../composables/useLibraryMarks';
import { useEventStore } from '../../stores/event';

/**
 * The Library (XN-4560, pulled forward into XN-4552 by Hunter, 2026-09-30):
 * the prototype's list (build 30 Sep 12:40, library()) — search, Everything /
 * Only for you, one row per file. A row opens the file's own page,
 * /hub/library/:id. Saved and the star (XN-4682) are the policyholder's own
 * marks; search filters within the tab and every count follows it (XN-4683).
 */

const eventStore = useEventStore();
const hubName = computed(() => eventStore.event?.value?.login_event_name || '');
const { filesOn, state, items, load } = usePortalLibrary();
const marks = useLibraryMarks();

const route = useRoute();
const router = useRouter();

// Back from a file's page lands on the list as it was left: the tab and the
// search live in the URL, the scroll in this history entry (the router keeps
// none without a scrollBehavior). A file opened inside the search pause leaves
// the URL behind the screen, so the leave also saves the tab and search in the
// entry; they are the newest, win over the URL once, and are then removed so a
// refresh reads the URL.
const { libraryTab, libraryQuery, libraryScroll, ...kept } = window.history.state || {};
const hasSaved = typeof libraryQuery === 'string';
const stripped = hasSaved || libraryScroll != null;
if (stripped) window.history.replaceState(kept, '');

const TABS = ['mine', 'saved'];
const tabFrom = (t) => (TABS.includes(t) ? t : 'all');
const fromUrl = (q) => ({ tab: tabFrom(q.tab), q: typeof q.q === 'string' ? q.q : '' });
const start = hasSaved ? { tab: tabFrom(libraryTab), q: libraryQuery } : fromUrl(route.query);
const tab = ref(start.tab);
const query = ref(start.q);
const searchInput = ref(null);
const libraryPath = route.path;

// The tab is written at once; the search only after 250ms without typing, so
// a keystroke is not a history write (Safari throws after ~100 in 30s) and
// does not wake anything watching the route. The write reads both refs when it
// fires, so it always carries the current tab and search. `lastWritten` is what
// the URL last got from here, so the route watcher can tell its echo (ignored,
// or it would clobber newer keystrokes) from a navigation (applied).
let writeTimer;
let lastWritten = fromUrl(route.query);
function writeUrl() {
  clearTimeout(writeTimer);
  if (tab.value === lastWritten.tab && query.value === lastWritten.q) return;
  lastWritten = { tab: tab.value, q: query.value };
  const next = {};
  if (tab.value !== 'all') next.tab = tab.value;
  if (query.value) next.q = query.value;
  router.replace({ query: next });
}
watch(tab, writeUrl);
watch(query, () => {
  clearTimeout(writeTimer);
  if (query.value !== lastWritten.q) writeTimer = setTimeout(writeUrl, 250);
});
// Clicking the shell's Library link while on a filtered list reuses this
// component; the screen follows the URL. (Leaving to a file page changes the
// query too, hence the path check.)
watch(() => route.query, (q) => {
  if (route.path !== libraryPath) return;
  const url = fromUrl(q);
  if (url.tab === lastWritten.tab && url.q === lastWritten.q) return;
  clearTimeout(writeTimer);
  lastWritten = url;
  tab.value = url.tab;
  query.value = url.q;
});
if (stripped) {
  // The router keeps its own copy of the history state and merges it back on
  // the next push, so the stripped keys would return: one replace refreshes it.
  if (hasSaved && (start.tab !== lastWritten.tab || start.q !== lastWritten.q)) writeUrl();
  else router.replace({ query: route.query, force: true });
}

let restoreTo = libraryScroll;
// On Saved the rows wait for the marks too, so the page is not tall enough to scroll until both are in.
watch([state, marks.state], async ([s, m]) => {
  if (s !== 'ready' || m === 'loading' || !restoreTo) return;
  const top = restoreTo;
  restoreTo = null;
  await nextTick();
  window.scrollTo(0, top);
});

onBeforeRouteLeave(() => {
  // A pending write would land on the file page's URL, and a router.replace
  // cannot run inside this guard: keep what the user left in the entry instead.
  clearTimeout(writeTimer);
  window.history.replaceState({ ...window.history.state, libraryTab: tab.value, libraryQuery: query.value, libraryScroll: window.scrollY }, '');
});

// Staff previewing have no marks, so no Saved tab; ?tab=saved reads as Everything for them.
const savedShown = computed(() => marks.state.value !== 'refused');
const activeTab = computed(() => (tab.value === 'saved' && !savedShown.value ? 'all' : tab.value));
const marksReady = computed(() => marks.state.value === 'ready');

const mineTotal = computed(() => items.value.filter((r) => r.mine).length);
const needle = computed(() => query.value.trim().toLowerCase());
// The search first, then the tab: every count is of the search's matches (XN-4683).
const matches = computed(() => items.value
  .filter((r) => !needle.value || String(r.name || '').toLowerCase().includes(needle.value)));
const inTab = (r, t) => t === 'all' || (t === 'mine' ? r.mine : marks.isSaved(r.id));
const counts = computed(() => ({
  all: matches.value.length,
  mine: matches.value.filter((r) => r.mine).length,
  saved: marksReady.value ? matches.value.filter((r) => marks.isSaved(r.id)).length : null,
}));
const visible = computed(() => matches.value.filter((r) => inTab(r, activeTab.value)));

function clearSearch() {
  query.value = '';
  searchInput.value?.focus();
}

// Unsaving inside Saved removes the row holding the focused star: hand focus on
// to the next row's star, else the previous one's, else the empty state's button.
const listEl = ref(null);
async function toggleSave(id) {
  const unsave = activeTab.value === 'saved' && marks.isSaved(id);
  const at = visible.value.findIndex((r) => r.id === id);
  marks.toggle(id);
  if (!unsave) return;
  await nextTick();
  const stars = listEl.value?.querySelectorAll('.lib-row__star');
  const target = stars?.length
    ? stars[Math.min(at, stars.length - 1)]
    : document.querySelector('[data-testid="show-everything"]') ?? searchInput.value;
  target?.focus();
}

function searchEverything() {
  tab.value = 'all';
  nextTick(() => searchInput.value?.focus());
}

function showEverything() {
  query.value = '';
  tab.value = 'all';
}

// Searching Only for you or Saved with no match there, when Everything has some.
const elsewhere = computed(() => (needle.value && activeTab.value !== 'all' && !visible.value.length ? counts.value.all : 0));

const emptyLine = computed(() => {
  if (needle.value) return 'Try a different search.';
  if (activeTab.value === 'saved') return 'Tap the star on anything to keep it here.';
  return activeTab.value === 'mine' ? 'Nothing has been sent to you by name yet.' : 'Nothing has been shared with you yet.';
});

const starFor = (r) => (savedShown.value ? marks.isSaved(r.id) : null);
</script>

<template>
  <section class="lib">
    <h1 class="lib__title">Your library</h1>
    <p class="lib__sub">Everything {{ hubName }} has shared with you, in one place.</p>

    <p v-if="filesOn === false" class="lib__calm">Nothing has been shared here yet.</p>

    <!-- Drawn while the Files switch is still being read, since it is almost
         always on: the search and tabs are there at once, and only the list
         waits, as rows shaped like the rows coming (CP Library had no loader). -->
    <template v-else>
      <label class="lib__search">
        <IconSearch color="currentColor" size="17" />
        <input ref="searchInput" v-model="query" type="search" placeholder="Search your library" aria-label="Search your library" />
        <button v-if="query" type="button" class="lib__clear" aria-label="Clear the search" data-testid="search-clear" @click="clearSearch">
          <IconClose color="currentColor" size="16" />
        </button>
      </label>

      <div class="lib__tabs" role="tablist">
        <button type="button" role="tab" :aria-selected="activeTab === 'all'" :class="{ 'is-on': activeTab === 'all' }" @click="tab = 'all'">
          Everything <span v-if="state === 'ready'" class="lib__n">{{ counts.all }}</span>
        </button>
        <button type="button" role="tab" :aria-selected="activeTab === 'mine'" :class="{ 'is-on': activeTab === 'mine' }" @click="tab = 'mine'">
          Only for you <span v-if="state === 'ready'" class="lib__n">{{ counts.mine }}</span>
        </button>
        <button v-if="savedShown" type="button" role="tab" :aria-selected="activeTab === 'saved'" :class="{ 'is-on': activeTab === 'saved' }" @click="tab = 'saved'">
          Saved <span v-if="state === 'ready' && counts.saved !== null" class="lib__n">{{ counts.saved }}</span>
        </button>
      </div>

      <Loading v-if="state === 'loading' || filesOn === null" variant="rows" :rows="5" data-testid="library-loading" />

      <div v-else-if="state === 'error'" class="lib__card lib__card--pad lib__error" role="alert">
        <p>Your library could not be loaded.</p>
        <XlButton variant="ghost" data-testid="library-retry" @click="load">Retry</XlButton>
      </div>

      <template v-else-if="state === 'ready'">
        <div v-if="activeTab === 'mine' && mineTotal" class="lib__card lib__card--pad lib__mine-note">
          <span class="lib__note-tile"><IconLock color="currentColor" size="22" /></span>
          <p><b>These are yours personally.</b> {{ hubName }} sent them to you by name — nobody else can see them.</p>
        </div>

        <div v-if="activeTab === 'saved' && marks.state.value === 'error'" class="lib__card lib__card--pad lib__error" role="alert">
          <p>Your saved items can't be shown right now.</p>
          <XlButton variant="ghost" data-testid="saved-retry" @click="marks.load">Retry</XlButton>
        </div>

        <!-- Marks still loading: Saved cannot yet say what is in it, so rows wait rather than a false empty state. -->
        <Loading v-else-if="activeTab === 'saved' && !marksReady" variant="rows" :rows="3" data-testid="library-saved-loading" />

        <div v-else-if="visible.length" ref="listEl" class="lib__card">
          <PortalLibraryRow
            v-for="r in visible"
            :key="r.id"
            :item="r"
            :saved="starFor(r)"
            :star-disabled="!marksReady"
            :is-new="marks.isNew(r.id)"
            @toggle-save="toggleSave"
          />
        </div>

        <div v-else class="lib__card lib__card--pad lib__empty" data-testid="library-empty">
          <IconFile color="currentColor" size="34" class="lib__empty-icon" />
          <b>Nothing here<template v-if="needle"> for “{{ query.trim() }}”</template></b>
          <template v-if="elsewhere">
            <p>{{ elsewhere }} in Everything.</p>
            <XlButton variant="ghost" data-testid="search-everything" @click="searchEverything">Search Everything</XlButton>
          </template>
          <template v-else>
            <p>{{ emptyLine }}</p>
            <XlButton v-if="needle || activeTab !== 'all'" variant="ghost" data-testid="show-everything" @click="showEverything">Show everything</XlButton>
          </template>
        </div>
      </template>
    </template>
  </section>
</template>

<style scoped>
/* Prototype library() and its .srch/.tabs/.card rules (build 30 Sep 12:40). Rows: PortalLibraryRow. */
.lib__title { font-size: 32px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.lib__sub { font-size: 16.5px; color: var(--xl-color-on-surface-variant); margin: 0 0 22px; }
.lib__calm { color: var(--xl-color-on-surface-variant); margin: 0; }

.lib__search {
  display: flex; align-items: center; gap: 9px; height: 42px; padding: 0 14px; margin-bottom: 18px;
  border-radius: 99px; background: #fff; border: 1px solid var(--xl-color-border); box-shadow: var(--xl-shadow-1);
  color: var(--xl-color-outline);
}
.lib__search input { flex: 1; min-width: 0; border: 0; outline: none; background: none; font: inherit; font-size: 14.5px; color: var(--xl-color-on-surface); }
.lib__search input::-webkit-search-cancel-button { display: none; }
.lib__search:focus-within { border-color: var(--xl-color-primary); }
.lib__clear { display: grid; place-items: center; border: 0; background: none; color: inherit; cursor: pointer; padding: 0; }

.lib__tabs { display: flex; gap: 28px; margin: 0 0 22px; border-bottom: 1px solid var(--xl-color-border); flex-wrap: wrap; }
.lib__tabs button {
  position: relative; padding: 0 0 12px; border: 0; background: none; cursor: pointer;
  font: inherit; font-size: 15px; font-weight: 600; color: var(--xl-color-on-surface-variant); white-space: nowrap;
}
.lib__tabs button::after { content: ''; position: absolute; left: 0; right: 0; bottom: -1px; height: 3px; border-radius: 3px 3px 0 0; background: transparent; }
.lib__tabs button:hover { color: var(--xl-color-on-surface); }
.lib__tabs button:hover::after { background: var(--xl-color-primary-fixed-dim); }
.lib__tabs button.is-on { color: var(--xl-color-primary); }
.lib__tabs button.is-on::after { background: var(--xl-color-primary); }
.lib__n {
  display: inline-block; margin-left: 7px; padding: 1px 8px; border-radius: 99px; font-size: 11.5px; font-weight: 700;
  background: var(--xl-color-border-subtle); color: var(--xl-color-outline); font-variant-numeric: tabular-nums;
}
.lib__tabs button.is-on .lib__n { background: var(--xl-color-primary-container); color: var(--xl-color-primary); }

.lib__card {
  background: #fff; border: 1px solid var(--xl-color-border); border-radius: var(--xl-radius-lg);
  padding: 8px 22px; box-shadow: var(--xl-shadow-1); margin-bottom: 14px;
}
.lib__card--pad { padding: 22px 24px; }

.lib__mine-note { display: flex; gap: 12px; align-items: flex-start; background: var(--xl-color-primary-container); border-color: var(--xl-color-primary-fixed-dim); }
.lib__mine-note p { margin: 0; font-size: 14px; color: var(--xl-color-on-primary-container); }
.lib__note-tile {
  width: 40px; height: 40px; flex: 0 0 40px; border-radius: 13px; display: grid; place-items: center;
  background: var(--xl-color-primary-container); color: var(--xl-color-primary);
}

.lib__empty { text-align: center; padding: 44px; display: flex; flex-direction: column; align-items: center; gap: 6px; }
.lib__empty-icon { color: var(--xl-color-outline-variant); margin-bottom: 4px; }
.lib__empty b { font-size: 16px; }
.lib__empty p { margin: 0 0 10px; font-size: 14px; color: var(--xl-color-outline); }
.lib__error { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.lib__error p { margin: 0; }

@media (max-width: 480px) {
  .lib__card { padding: 4px 14px; }
  .lib__card--pad { padding: 18px; }
}
</style>
